// src/pages/Workers/Departments/UpdateDepartment.jsx
import { useEffect, useState } from "react";
import { Drawer, Box, IconButton } from "@mui/material";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import save from "../../../../assets/save.svg";
import { toast, Toaster } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useFormik } from "formik";


const UpdateDepartment = ({ open, onClose, onSuccess, department }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const formik = useFormik({
    initialValues: {
      departmentName: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.departmentName || values.departmentName.trim() === "") {
        errors.departmentName = t("departments.addModal.departmentNameRequired") || "Department name is required";
      }
      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      const name = values.departmentName.trim();
      if (!name) {
        return;
      }
      if (!department?.id) {
        toast.error("Missing department id.");
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/departments/${department.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify({ name }),
        });

        const data = await res.json();
        if (!res.ok || data?.error) {
          throw new Error(data?.message || "Failed to update department.");
        }

        toast.success(t("departments.editModal.departmentUpdated", "Department updated"));
        onClose?.();
        onSuccess?.(); // trigger parent refetch (with small header loader if you wired it)
      } catch (err) {
        console.error("Error updating department:", err);
        toast.error(err.message || "Error while updating department.");
      } finally {
        setLoading(false);
      }
    },
  });

  // Prefill when opening for edit
  useEffect(() => {
    if (open && department) {
      formik.setValues({
        departmentName: department?.name || "",
      });
      formik.setTouched({ departmentName: false });
      formik.setErrors({});
    }
  }, [open, department]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Toaster />
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 1.3,
          borderBottom: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
     {t("common.updateDepartment")}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
        {/* Department Name */}
        <Box mb={2}>
          <label className="field_label">
            {t("departments.addModal.departmentName", "Department Name")}{" "}
            <span className="red_asterisk">*</span>
          </label>
          <input
            className="project_input"
            placeholder={t("departments.addModal.departmentNamePlaceholder", "Enter department name")}
            style={{ 
              height: "40px", 
              width: "100%", 
              marginTop: 6,
              border: formik.touched.departmentName && formik.errors.departmentName
                ? "2px solid #d32f2f"
                : "2px solid #091E4224"
            }}
            name="departmentName"
            value={formik.values.departmentName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.departmentName && formik.errors.departmentName && (
            <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
              {formik.errors.departmentName}
            </span>
          )}
        </Box>

        {/* Update Button */}
        <Box sx={{ mt: 3 }}>
          <CustomButton
            onClick={formik.handleSubmit}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {loading && (
                  <CircularProgress size={18} thickness={4} sx={{ color: "#ffffff" }} />
                )}
                <img src={save} alt="save" style={{ width: 16, height: 16 }} />
                {t("common.update", "Update")}
              </Box>
            }
            size="medium"
            disabled={loading}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default UpdateDepartment;
